import { css } from '@emotion/react';
import { faStar } from '@fortawesome/pro-solid-svg-icons';
import { type FC } from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LabelInCircleComponent } from '~/_shared/baseComponents/labelInShape/labelInCircleComponent';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { useTranslation } from '~/_shared/utils/hooks';
import adobeLogo from '../../../../../assets/images/customerLogos/adobe.png';
import amazonLogo from '../../../../../assets/images/customerLogos/amazon.png';
import cbreLogo from '../../../../../assets/images/customerLogos/cbre.png';
import cocaColaLogo from '../../../../../assets/images/customerLogos/cocaCola.png';
import hiltonLogo from '../../../../../assets/images/customerLogos/hilton.png';
import pepsicoLogo from '../../../../../assets/images/customerLogos/pepsico.png';
import siemensLogo from '../../../../../assets/images/customerLogos/siemens.png';
import ubsLogo from '../../../../../assets/images/customerLogos/ubs.png';

const BRAND_BLUE = '#8ed0ff';
const BRAND_GREEN = '#63C01C';
const BRAND_STAR = '#FFB42E';
const NO_MARGIN = 0;
const STAR_COUNT = 5;

const CUSTOMER_LOGOS = [
  { name: 'PepsiCo', src: pepsicoLogo },
  { name: 'Coca-Cola', src: cocaColaLogo },
  { name: 'Siemens', src: siemensLogo },
  { name: 'Adobe', src: adobeLogo },
  { name: 'Amazon', src: amazonLogo },
  { name: 'CBRE', src: cbreLogo },
  { name: 'Hilton', src: hiltonLogo },
  { name: 'UBS', src: ubsLogo },
] as const;

const panelStyle = css({
  color: '#fff',
  cursor: 'default',
  marginLeft: '40px',
});

const headlineStyle = css({
  fontSize: 'clamp(1.7rem, 2.4vw, 2.3rem)',
  fontWeight: 800,
  letterSpacing: '-0.025em',
  margin: NO_MARGIN,
  maxWidth: '24ch',
});

const stepsListStyle = css({
  display: 'grid',
  gap: 22,
  listStyle: 'none',
  margin: NO_MARGIN,
  padding: 0,
});

const stepItemStyle = css({
  alignItems: 'flex-start',
  display: 'flex',
  gap: 16,
});

const SIZE = 30;
const stepNumberColors = {
  fillColor: 'rgba(255, 255, 255, .14)',
  fontColor: BRAND_BLUE,
};
const stepNumberExtraStyle = css({
  border: '1px solid rgba(255, 255, 255, .25)',
  flex: 'none',
  fontSize: 13,
  fontWeight: 800,
  marginTop: 1,
});

const stepTitleStyle = css({
  display: 'block',
  fontSize: 17,
  fontWeight: 700,
  letterSpacing: '-0.01em',
  marginBottom: 4,
});

const stepDescriptionStyle = css({
  color: '#c2d8f5',
  display: 'block',
  fontSize: 14,
  fontWeight: 400,
  lineHeight: 1.5,
  maxWidth: '56ch',
});

const testimonialStyle = css({
  background: 'rgba(255, 255, 255, .09)',
  border: '1px solid rgba(255, 255, 255, .2)',
  borderLeft: `4px solid ${BRAND_GREEN}`,
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  margin: NO_MARGIN,
  padding: '22px 26px 20px 28px',
  position: 'relative',
});

const testimonialQuoteMarkStyle = css({
  color: 'rgba(255, 255, 255, .14)',
  fontSize: '4.6rem',
  fontWeight: 800,
  lineHeight: 1,
  pointerEvents: 'none',
  position: 'absolute',
  right: 20,
  top: 2,
});

const starsRowStyle = css({
  color: BRAND_STAR,
  fontSize: 13,
});

const testimonialTextStyle = css({
  fontSize: '1.15rem',
  fontWeight: 700,
  letterSpacing: '-0.015em',
  lineHeight: 1.4,
  margin: NO_MARGIN,
  maxWidth: '44ch',
  position: 'relative',
});

const testimonialAttributionStyle = css({
  color: '#cfe2fb',
  fontSize: 13,
  fontWeight: 600,
  letterSpacing: '.01em',
});

const testimonialCompanyStyle = css({
  color: '#9dbbe3',
  fontWeight: 500,
});

const logoStripStyle = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: '24px 32px',
  alignItems: 'center',
  maxWidth: 620,
});

const logoImageStyle = css({
  maxHeight: 24,
  maxWidth: '100%',
  width: 'auto',
  height: 'auto',
  objectFit: 'contain',
  filter: 'brightness(0) invert(1)',
  opacity: .62,
});

export const RegisterMarketingPanelComponent: FC = () => {
  const [t] = useTranslation();

  return (
    <Stack
      css={panelStyle}
      gap={32}
    >
      <h1 css={headlineStyle}>{t('registerPage.marketing.headline')}</h1>

      <ol css={stepsListStyle}>
        <li css={stepItemStyle}>
          <LabelInCircleComponent
            colors={stepNumberColors}
            css={stepNumberExtraStyle}
            size={SIZE}
          >
            1
          </LabelInCircleComponent>
          <span>
            <span css={stepTitleStyle}>{t('registerPage.marketing.step1.title')}</span>
            <span css={stepDescriptionStyle}>{t('registerPage.marketing.step1.description')}</span>
          </span>
        </li>

        <li css={stepItemStyle}>
          <LabelInCircleComponent
            colors={stepNumberColors}
            css={stepNumberExtraStyle}
            size={SIZE}
          >
            2
          </LabelInCircleComponent>
          <span>
            <span css={stepTitleStyle}>{t('registerPage.marketing.step2.title')}</span>
            <span css={stepDescriptionStyle}>{t('registerPage.marketing.step2.description')}</span>
          </span>
        </li>

        <li css={stepItemStyle}>
          <LabelInCircleComponent
            colors={stepNumberColors}
            css={stepNumberExtraStyle}
            size={SIZE}
          >
            3
          </LabelInCircleComponent>
          <span>
            <span css={stepTitleStyle}>{t('registerPage.marketing.step3.title')}</span>
            <span css={stepDescriptionStyle}>{t('registerPage.marketing.step3.description')}</span>
          </span>
        </li>
      </ol>

      <blockquote css={testimonialStyle}>
        <span
          aria-hidden
          css={testimonialQuoteMarkStyle}
        >
          &ldquo;
        </span>

        <Inline
          css={starsRowStyle}
          gap={3}
        >
          {Array.from({ length: STAR_COUNT }).map((_, index) => (
            <FontAwesomeIcon
              icon={faStar}
              key={index}
            />
          ))}
        </Inline>

        <p css={testimonialTextStyle}>{t('registerPage.marketing.testimonial.quote')}</p>

        <div css={testimonialAttributionStyle}>
          {t('registerPage.marketing.testimonial.author')}
          {' · '}
          <span css={testimonialCompanyStyle}>{t('registerPage.marketing.testimonial.company')}</span>
        </div>
      </blockquote>

      <div css={logoStripStyle}>
        {CUSTOMER_LOGOS.map(logo => (
          <img
            alt={logo.name}
            css={logoImageStyle}
            key={logo.name}
            src={logo.src}
          />
        ))}
      </div>
    </Stack>
  );
};
